import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "@/lib/utils";

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };

// Componente UserAvatar con lógica de imagen y fallback a iniciales
interface UserAvatarProps {
  user: // Usuario de la API
    | {
        first_name: string;
        last_name: string;
        picture?: string;
        roleSpecificData?: {
          picture?: string;
        };
      }
    // Usuario de Auth0
    | {
        name?: string;
        nickname?: string;
        email?: string;
        picture?: string;
      };
  size?: "sm" | "md" | "lg";
  className?: string;
  fallbackSrc?: string;
}

export function UserAvatar({
  user,
  size = "md",
  className,
  fallbackSrc,
}: UserAvatarProps) {
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
  };

  const getSizeClasses = (size: "sm" | "md" | "lg") => {
    switch (size) {
      case "sm":
        return "h-8 w-8 text-xs";
      case "md":
        return "h-10 w-10 text-sm";
      case "lg":
        return "h-12 w-12 text-base";
      default:
        return "h-10 w-10 text-sm";
    }
  };

  // Detectar qué tipo de datos tenemos y extraer información
  const isApiUser = "first_name" in user && "last_name" in user;
  const isSupabaseUserData =
    "picture" in user &&
    !("name" in user) &&
    !("first_name" in user) &&
    !("email" in user);

  let initials: string;
  let profilePicture: string | undefined;
  let altText: string;

  if (isApiUser) {
    // Usuario de la API completa
    initials = getInitials(user.first_name, user.last_name);
    profilePicture = user.roleSpecificData?.picture || user.picture;
    altText = `${user.first_name} ${user.last_name}`;
  } else if (isSupabaseUserData) {
    // Datos de roleSpecificData de Supabase (solo tiene picture y otros campos específicos)
    profilePicture = user.picture;
    // Para las iniciales, necesitamos datos del usuario o usar valores por defecto
    initials = "US"; // Usuario genérico hasta que tengamos más info
    altText = "Usuario";
  } else {
    // Usuario de Auth0
    const userName = user.name || user.nickname || user.email || "Usuario";
    const nameParts = userName.includes("@")
      ? userName.split("@")[0].split(" ")
      : userName.split(" ");

    const firstName = nameParts[0] || "U";
    const lastName =
      nameParts[1] || (nameParts[0] ? nameParts[0][1] || "" : "");

    initials = getInitials(firstName, lastName);
    profilePicture = user.picture;
    altText = userName;
  }

  const sizeClasses = getSizeClasses(size);

  return (
    <Avatar className={`${sizeClasses} ${className || ""}`}>
      <AvatarImage
        src={profilePicture || fallbackSrc}
        alt={altText}
        className="object-cover"
        onError={(e) => {
          if (fallbackSrc && e.currentTarget.src !== fallbackSrc) {
            e.currentTarget.src = fallbackSrc;
          }
        }}
      />
      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-medium">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
